import { ModelEntitySearchResponseItem } from '@upright/common/model/search/types';
import { friendlyCountryName } from '@upright/common/model/texts/friendly-country-name';
import { uppercaseFirstLetter } from '@upright/ui/untidy/texts';
import styled from 'styled-components';
import { BouColor } from '../../../../../../generic/global/bou-colors';

interface Props {
  item: ModelEntitySearchResponseItem;
}

/**
 * Compact "country · listing status" tag shown on company rows when the search
 * box opts into disambiguation metadata (the self-serve portfolio picker). It
 * is what lets a user tell same-named entities apart — e.g. Helen Oy (Finland ·
 * Private) from Helen Of Troy (United States · Public) — which the default row
 * (name + description) does not surface. Unlike `CountryBlock`, country is
 * shown even when a description is present, since the description alone does
 * not disambiguate domicile.
 */
export function DisambiguationBlock(props: Props) {
  const { item } = props;
  const country = item.country ? uppercaseFirstLetter(friendlyCountryName(item.country)) : undefined;
  const listing = listingStatusLabel(item.listingStatus);
  const parts = [country, listing].filter((p): p is string => !!p);
  if (parts.length === 0) {
    return <Root />;
  }
  return <Root>{parts.join(' · ')}</Root>;
}

function listingStatusLabel(listingStatus: string | undefined): string | undefined {
  switch (listingStatus) {
    case 'PUBLIC':
      return 'Public';
    case 'PRIVATE':
      return 'Private';
    case undefined:
      return undefined;
    default:
      return undefined;
  }
}

const Root = styled.div`
  color: ${BouColor.PLATFORM_TEXT_GRAY_LIGHT};
  flex-shrink: 0;
  font-weight: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: 8px;
`;
